import { call, put, ForkEffect, takeLeading, takeLatest, takeEvery } from 'redux-saga/effects';
import { callbackResolver } from 'src/utils/reduxHelpers';
import { AxiosError } from 'axios';

import { workspacesTypes } from '../types';
import {
  addWorkspaceAccessHelper,
  addWorkspaceHelper,
  deleteWorkspaceAccessHelper,
  deleteWorkspaceHelper,
  editWorkspaceAccessHelper,
  editWorkspaceHelper,
  getCompanyWorkspacesHelper,
  getUserWorkspacesHelper,
  getWorkspaceAccessesHelper
} from './sagaHelpers/workspacesApiHelpers';
import {
  TAddWorkspaceAccessAction,
  TAddWorkspaceAction,
  TDeleteWorkspaceAccessAction,
  TDeleteWorkspaceAction,
  TEditWorkspaceAccessAction,
  TEditWorkspaceAction,
  TGetCompanyWorkspacesAction,
  TGetUserWorkspacesAction,
  TGetWorkspacesAccessesAction
} from 'src/interfaces/workspacesReduxShapes';
import { putCompanyWorkspaces, putUserWorkspaces, putWorkspaceAccesses } from '../actions/workspacesActions';

const errorHandler = (error) => {
  const axiosError = error as AxiosError;
  return axiosError.response?.data || error;
};

function* getCompanyWorkspacesWorker({ payload }: TGetCompanyWorkspacesAction) {
  try {
    const { data } = yield call(getCompanyWorkspacesHelper);
    yield put(putCompanyWorkspaces(data.data));

    callbackResolver(payload?.onSuccess, data.data);
  } catch (error) {
    yield put(putCompanyWorkspaces([]));

    const errorData = errorHandler(error);
    callbackResolver(payload?.onError, errorData);
    console.error('getCompanyWorkspacesWorker', errorData || error);
  }
}

function* getUserWorkspacesWorker({ payload }: TGetUserWorkspacesAction) {
  try {
    const { data } = yield call(getUserWorkspacesHelper);

    yield put(putUserWorkspaces(data.data));

    callbackResolver(payload?.onSuccess, data.data);
  } catch (error) {
    yield put(putUserWorkspaces([]));

    const errorData = errorHandler(error);
    console.error('getUserWorkspacesWorker ERROR:', errorData || error);
    callbackResolver(payload?.onError, errorData);
  }
}

function* addWorkspaceWorker({ payload }: TAddWorkspaceAction) {
  try {
    yield call(addWorkspaceHelper, payload.body);

    // After successfully adding a workspace, fetch the updated list
    const { data } = yield call(getCompanyWorkspacesHelper);
    yield put(putCompanyWorkspaces(data.data));

    // Only after updating the Redux store, call the success callback
    callbackResolver(payload?.onSuccess);
  } catch (error) {
    const errorData = errorHandler(error);
    callbackResolver(payload?.onError, errorData);
    console.error('addWorkspaceWorker', errorData || error);
  }
}

function* editWorkspaceWorker({ payload }: TEditWorkspaceAction) {
  try {
    yield call(editWorkspaceHelper, payload.id, payload.body);

    // After successfully editing a workspace, fetch the updated list
    const { data } = yield call(getCompanyWorkspacesHelper);
    yield put(putCompanyWorkspaces(data.data));

    // Only after updating the Redux store, call the success callback
    callbackResolver(payload?.onSuccess);
  } catch (error) {
    const errorData = errorHandler(error);
    callbackResolver(payload?.onError, errorData);
    console.error('editWorkspaceWorker', errorData || error);
  }
}

function* deleteWorkspaceWorker({ payload }: TDeleteWorkspaceAction) {
  try {
    yield call(deleteWorkspaceHelper, payload.id);

    // After successfully deleting a workspace, fetch the updated list
    const { data } = yield call(getCompanyWorkspacesHelper);
    yield put(putCompanyWorkspaces(data.data));

    // Only after updating the Redux store, call the success callback
    callbackResolver(payload?.onSuccess);
  } catch (error) {
    const errorData = errorHandler(error);
    callbackResolver(payload?.onError, errorData);
    console.error('deleteWorkspaceWorker', errorData || error);
  }
}

function* getUserWorkspaceAccessesWorker({ payload }: TGetWorkspacesAccessesAction) {
  const initialData = { list: [], workspaceId: payload.workspaceId };
  try {
    const { data: workspacesData } = yield call(getCompanyWorkspacesHelper);
    yield put(putCompanyWorkspaces(workspacesData.data));

    const { data } = yield call(getWorkspaceAccessesHelper, payload.workspaceId);

    if (data && data.data) {
      yield put(
        putWorkspaceAccesses({
          list: data.data,
          workspaceId: payload.workspaceId
        })
      );
      callbackResolver(payload?.onSuccess, data.data);
    } else {
      console.error('Unexpected workspace accesses data structure:', data);
      yield put(putWorkspaceAccesses(initialData));
      callbackResolver(payload?.onError, { message: 'Invalid data structure received' });
    }
  } catch (error) {
    const errorData = errorHandler(error);
    console.error('getUserWorkspaceAccessesWorker ERROR:', errorData || error);

    yield put(putWorkspaceAccesses(initialData));

    callbackResolver(payload?.onError, errorData);
  }
}

function* addWorkspaceAccessWorker({ payload }: TAddWorkspaceAccessAction) {
  try {
    yield call(addWorkspaceAccessHelper, payload.workspaceId, payload.body);

    // After successfully adding an access, fetch the updated list
    const { data } = yield call(getWorkspaceAccessesHelper, payload.workspaceId);
    yield put(putWorkspaceAccesses({ list: data.data, workspaceId: payload.workspaceId }));

    // Only after updating the Redux store, call the success callback
    callbackResolver(payload?.onSuccess);
  } catch (error) {
    const errorData = errorHandler(error);
    callbackResolver(payload?.onError, errorData);
    console.error('addWorkspaceWorker', errorData || error);
  }
}

function* editWorkspaceAccessWorker({ payload }: TEditWorkspaceAccessAction) {
  try {
    yield call(editWorkspaceAccessHelper, payload.workspaceId, payload.userId, payload.body);

    // After successfully adding an access, fetch the updated list
    const { data } = yield call(getWorkspaceAccessesHelper, payload.workspaceId);
    yield put(putWorkspaceAccesses({ list: data.data, workspaceId: payload.workspaceId }));

    // Only after updating the Redux store, call the success callback
    callbackResolver(payload?.onSuccess);
  } catch (error) {
    const errorData = errorHandler(error);
    callbackResolver(payload?.onError, errorData);
    console.error('editWorkspaceWorker', errorData || error);
  }
}

function* deleteWorkspaceAccessWorker({ payload }: TDeleteWorkspaceAccessAction) {
  try {
    yield call(deleteWorkspaceAccessHelper, payload.workspaceId, payload.userId);

    // After successfully deleting an access, fetch the updated list
    const { data } = yield call(getWorkspaceAccessesHelper, payload.workspaceId);
    yield put(putWorkspaceAccesses({ list: data.data, workspaceId: payload.workspaceId }));

    // Only after updating the Redux store, call the success callback
    callbackResolver(payload?.onSuccess);
  } catch (error) {
    const errorData = errorHandler(error);
    callbackResolver(payload?.onError, errorData);
    console.error('deleteWorkspaceWorker', errorData || error);
  }
}

export function* workspacesWatcher(): Generator<ForkEffect<never>, void, unknown> {
  yield takeLatest(workspacesTypes.GET_COMPANY_WORKSPACES, getCompanyWorkspacesWorker);
  yield takeLatest(workspacesTypes.GET_USER_WORKSPACES, getUserWorkspacesWorker);
  yield takeLeading(workspacesTypes.EDIT_COMPANY_WORKSPACE, editWorkspaceWorker);
  yield takeLeading(workspacesTypes.ADD_COMPANY_WORKSPACE, addWorkspaceWorker);
  yield takeLeading(workspacesTypes.DELETE_COMPANY_WORKSPACE, deleteWorkspaceWorker);
  yield takeEvery(workspacesTypes.GET_COMPANY_WORKSPACES_ACCESS, getUserWorkspaceAccessesWorker);
  yield takeLeading(workspacesTypes.EDIT_COMPANY_WORKSPACE_ACCESS, editWorkspaceAccessWorker);
  yield takeLeading(workspacesTypes.ADD_COMPANY_WORKSPACE_ACCESS, addWorkspaceAccessWorker);
  yield takeLeading(workspacesTypes.DELETE_COMPANY_WORKSPACE_ACCESS, deleteWorkspaceAccessWorker);
}
